/**
 * What the two sidebars share: the shape of an area, the link that draws one, and the TEAM group.
 *
 * Extracted from `Shell.tsx` when the International standard got a sidebar of its own (ADR 0095).
 * `InternationalNav.tsx` needs the same link and the same TEAM entries as the FlowZa Sign nav, and
 * importing them from the shell would have made the two modules import each other — a module whose
 * top-level `AREAS.filter(…)` runs while the shell's `AREAS` is still in its temporal dead zone.
 */
import { NavLink } from 'react-router-dom'
import { Icon, type IconName } from '@flowza/ui'

/**
 * `workspace` here is a UX GROUPING, not a team.
 *
 * It predates teams by eight phases and means "the things you work on", against `organization`
 * meaning "the things you configure". Teams arrived in ADR 0066 and are the `team` group below.
 * The collision of the word is unfortunate and renaming the old group would touch every test that
 * reads the nav; saying which is which here is the cheaper honest fix.
 */
export type AreaGroup = 'workspace' | 'team' | 'organization'

export type Area = {
  readonly to: string
  readonly label: string
  readonly icon: IconName
  readonly group: AreaGroup
  /** Match only the exact path — the dashboard, so it is not "active" under every child route. */
  readonly end?: boolean
  /** False until the feature that fills the area lands; the route resolves to the dashboard meanwhile. */
  readonly ready: boolean
  /**
   * The catalog keys this area's screens read under, ANY of which makes it worth offering (ADR
   * 0196). Absent means every member. What is offered, never what is allowed: the API gates each
   * route with the same keys, and a deep link past the nav lands on a screen that says who may.
   */
  readonly requires?: readonly string[]
}

export type Workspace = {
  readonly id: string
  readonly name: string
  readonly is_default: boolean
}

/**
 * The two SHELVES — the archive and the bin — which are NOT in either sidebar (founder, 2026-09-16:
 * 'Left side remove "Archive" and "Trash" module, as already this is there in the Inbox').
 *
 * They were nav rows under Documents in both standards, and the Inbox's Archived and Deleted tabs
 * now hold the same envelopes with the same restore, so the rows were a second door to a room the
 * reader is already standing in.
 *
 * The ROUTES stay, and so do these entries: `/app/archive` and `/app/trash` are still what the
 * library's own shelf rail links to, they are still somebody's bookmark, and they are still the
 * honest answer when a reader types "trash" into ⌘K. Removing them from the palette as well would
 * have turned "one fewer row in the sidebar" into "the archive is gone", which is not what was
 * asked. So both sidebars simply do not draw them, and the palette lists them from here.
 */
export const SHELF_AREAS: readonly Area[] = [
  { to: '/app/archive', label: 'Archive', icon: 'folder', group: 'workspace', ready: true },
  { to: '/app/trash', label: 'Trash', icon: 'trash', group: 'workspace', ready: true },
]

/**
 * The TEAM group. Rendered only when the caller sits on at least one team, and pointing at the
 * SAME routes as the Workspace group — Documents is Documents. The difference is the `workspace`
 * query parameter, which narrows the list to the active team; the routes have always been narrowed
 * by the caller's own scope (ADR 0066 / D11) and this is the visible half of that.
 *
 * Duplicating the destinations rather than moving them is deliberate: somebody on three teams
 * still wants "everything I can see", and a nav that only offered per-team views would have
 * removed a capability to add one.
 */
export const TEAM_AREAS: readonly Area[] = [
  {
    to: '/app/documents?scope=team',
    label: 'Documents',
    icon: 'file-text',
    group: 'team',
    ready: true,
  },
  {
    to: '/app/templates?scope=team',
    label: 'Templates',
    icon: 'template',
    group: 'team',
    ready: true,
    // `GET /v1/templates` is `templates.create`, which a viewer and a billing admin do not hold.
    requires: ['templates.create'],
  },
]

/**
 * One area in the left nav.
 *
 * The active state is a RAIL plus a tint, not a tint alone. A tinted pill on its own is the state
 * every console uses and it is the weakest signal in the list — at 10% alpha on a sunken sidebar it
 * is roughly the same value as the hover it has to be distinguished from. A 2 px bar hard against
 * the sidebar's edge is a different KIND of mark rather than a stronger version of the same one, so
 * it survives a tenant's accent being pale, and it reads from the corner of the eye.
 *
 * `ease-standard` on the rail so it grows rather than appearing, which is the difference between a
 * navigation that feels built and one that feels assembled.
 */
export function AreaLink({
  area,
  onNavigate,
}: {
  readonly area: Area
  readonly onNavigate: () => void
}) {
  return (
    <NavLink
      to={area.to}
      end={area.end ?? false}
      onClick={onNavigate}
      className={({ isActive }) =>
        `h-row group relative flex items-center gap-2.5 rounded-md pl-3.5 pr-2.5 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-accent/10 text-accent'
            : 'text-text-muted hover:bg-surface-raised hover:text-text-primary'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={`bg-accent ease-standard absolute inset-y-1.5 left-0 w-0.5 rounded-full transition-opacity duration-150 ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <Icon name={area.icon} size={16} />
          <span className="flex-1 truncate">{area.label}</span>
          {!area.ready ? (
            <span className="border-border-subtle text-text-muted rounded-sm border px-1 text-xs leading-4">
              Soon
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  )
}
